/**
 * The worked example the /about page is built around.
 *
 * Five invented listings — none of these companies exist — stand in for a
 * board so the mechanic can be shown rather than described. Only the five
 * lifetime totals are written down here. Every other figure on the page is
 * derived from them through `@bidtohire/shared`, so the arithmetic the page
 * teaches is the arithmetic the payment code performs.
 */

import { nextMinimumCents, topUpCents, type CompanyRef } from '@bidtohire/shared';

export interface ExampleListing {
  /** Enough of a company for `CompanyMark` to draw its fallback square. */
  readonly company: Pick<CompanyRef, 'name' | 'slug'>;
  /** Which mark colour this invented company takes, so five reads as five. */
  readonly markIndex: number;
  readonly title: string;
  /**
   * The taxonomy path, written exactly as a real board row writes it. The
   * companies are invented; the taxonomy is the site's own navigation
   * vocabulary, so a shortened category here sends a reader looking for a
   * board that does not exist.
   */
  readonly scope: string;
  readonly salary: string;
  /** Lifetime total paid, in cents. */
  readonly totalCents: number;
}

/** The board at rest, highest total first. */
export const EXAMPLE_BOARD: readonly ExampleListing[] = [
  {
    company: { name: 'Northbeam Systems', slug: 'northbeam-systems' },
    markIndex: 0,
    title: 'Staff Software Engineer, Payments Infrastructure',
    scope: 'Software Engineering → Backend',
    salary: '$255k – $310k',
    totalCents: 290_500,
  },
  {
    company: { name: 'Vantable', slug: 'vantable' },
    markIndex: 1,
    title: 'Senior Frontend Engineer, Trading UI',
    scope: 'Software Engineering → Frontend',
    salary: '$210k – $260k',
    totalCents: 234_000,
  },
  {
    company: { name: 'Cadence Robotics', slug: 'cadence-robotics' },
    markIndex: 2,
    title: 'Principal Product Designer',
    scope: 'Design, Creative & Media → Product Design',
    salary: '$205k – $250k',
    totalCents: 188_000,
  },
  {
    company: { name: 'Halden AI', slug: 'halden-ai' },
    markIndex: 7,
    title: 'Member of Technical Staff, Inference',
    scope: 'Data & AI → AI/ML Research',
    salary: '$240k – $320k',
    totalCents: 124_000,
  },
  {
    company: { name: 'Merit Health', slug: 'merit-health' },
    markIndex: 4,
    title: 'Engineering Manager, Platform',
    scope: 'Software Engineering → Engineering Management',
    salary: '$195k – $235k',
    totalCents: 90_000,
  },
] as const;

/** The listing that climbs, and the one it passes. */
export const LEADER = EXAMPLE_BOARD[0] as ExampleListing;
export const THIRD = EXAMPLE_BOARD[2] as ExampleListing;
export const CHALLENGER = EXAMPLE_BOARD[3] as ExampleListing;

/** How many places the challenger climbs, and where it lands. */
export const CHALLENGER_INDEX = 3;

/** Every derived figure the page prints, computed the way the API computes it. */
export const EXAMPLE = {
  /** The total that passes the leader: the leader's total + $1. */
  firstPlaceTotalCents: nextMinimumCents(LEADER.totalCents),
  /** The total that passes third: that row's total + $1. */
  claimThirdTotalCents: nextMinimumCents(THIRD.totalCents),
  /** What the challenger actually pays: the difference, never the whole total. */
  topUpCents: topUpCents(CHALLENGER.totalCents, nextMinimumCents(LEADER.totalCents)),
  /** What it would cost the old leader to take the position back afterwards. */
  retakeTotalCents: nextMinimumCents(nextMinimumCents(LEADER.totalCents)),
} as const;
